import { useStores } from '@littlebird/sdk';
import {
  deviceCount,
  platformName,
  type RanWithoutDevice,
  type ReachOutWait,
  type RoutineRunViewModel,
} from '@littlebird/ui-utils';
import { observer } from 'mobx-react-lite';
import {
  useCallback,
  useLayoutEffect,
  useState,
  type ComponentProps,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { SHIMMER_TEXT_CLASSES } from '@/components/blocks/Message/reasoning/ShimmerCollapsibleHeader';
import { StaffBackendLabel } from '@/components/blocks/staff-backend-label';
import { Button, Text } from '@/components/ui';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Skeleton } from '@/components/ui/skeleton';
import { Markdown } from '@/components/ui/smoothdown/Markdown';
import { CanvasActionCardPlacementProvider } from '@/features/action-cards/CanvasActionCardPlacementProvider';
import { ReportActionCards } from '@/features/action-cards/components/ReportActionCards';
import { useOpenChat } from '@/features/chat/use-open-chat';
import { DeviceIcon } from '@/features/routines/components/device-icon';
import { formatDeadline } from '@/features/routines/components/waiting-run-copy';
import { ReportArtifact } from '@/features/routines/report-artifact';
import { type ReportBody } from '@/features/routines/report-body';
import { ReportCostFooter } from '@/features/routines/report-cost-footer';
import { formatNoteDate } from '@/features/routines/routines.utils';
import { cn } from '@/lib/utils';
import { settingsStore } from '@/stores/settings.store';
import { uiStore } from '@/stores/ui.store';
import { userStore } from '@/stores/user.store';

interface RoutineRunEntryProps {
  run: RoutineRunViewModel;
  reportBody: ReportBody;
  actions?: ReactNode;
  isRefreshingNarrative?: boolean;
  onCanvasReadyChange?: (isReady: boolean) => void;
}

const RunSkeletonLines = () => (
  <YStack className="gap-2">
    <Skeleton className="h-4 w-3/4 rounded" />
    <Skeleton className="h-4 w-full rounded" />
    <Skeleton className="h-4 w-5/6 rounded" />
  </YStack>
);

const RunLoading = () => (
  <YStack className="gap-4" aria-label="Routine run is loading">
    <Text
      className={cn(
        'w-fit bg-clip-text text-sm font-medium text-transparent',
        ...SHIMMER_TEXT_CLASSES
      )}
    >
      Running this routine…
    </Text>
    <RunSkeletonLines />
  </YStack>
);

const DeviceSlug = ({
  className,
  children,
}: {
  className?: string;
  children: string;
}) => (
  <span className={cn('font-mono text-xs [overflow-wrap:anywhere]', className)}>
    {children}
  </span>
);

/** An action in a muted line, drawn as a link */
const TextLink = ({
  className,
  ...props
}: Omit<ComponentProps<'button'>, 'type'>) => (
  <button
    type="button"
    className={cn(
      'focus-ring whitespace-nowrap rounded-sm leading-5 text-foreground-subtle underline underline-offset-[3px] hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60',
      className
    )}
    {...props}
  />
);

/** The one shell every status box in the run view shares */
const StatusBox = ({
  icon,
  title,
  body,
  role,
  className,
  children,
}: {
  icon: ReactNode;
  title: string;
  body: string;
  role?: 'alert';
  className?: string;
  /** Actions, under the body */
  children?: ReactNode;
}) => (
  <XStack
    role={role}
    className={cn(
      'items-start gap-3 rounded-xl border border-border-strong bg-surface px-4 py-3.5',
      className
    )}
  >
    <span className="mt-0.5 flex text-foreground-subtle">{icon}</span>
    <YStack className="min-w-0 flex-1 gap-0.5">
      <Text className="text-sm font-medium">{title}</Text>
      <Text className="text-[13px] leading-relaxed text-foreground-muted">
        {body}
      </Text>
      {children}
    </YStack>
  </XStack>
);

const FINISH_FAILED_MESSAGE = 'Could not finish the run. Try again.';

/** What a run waits on its user for, the way to answer it, and the way to finish without them */
const UserWaitBox = observer(
  ({
    run,
    wait,
    deadline,
  }: {
    run: RoutineRunViewModel;
    wait: ReachOutWait;
    deadline: Date;
  }) => {
    const { reports } = useStores();
    const { openChat, isOpening } = useOpenChat();
    const isFinishing = reports.isOperationPending('finishWithoutMe', run.id);
    const chatId = run.chatId;
    const isReply = wait.kind === 'reply';

    const finish = (stopAsking: boolean) =>
      void reports
        .finishWithoutMe(run.id, { stopAsking })
        .catch(() => toast.error(FINISH_FAILED_MESSAGE));

    if (isFinishing) {
      return (
        <Text
          className={cn(
            'w-fit bg-clip-text text-[13px] text-transparent',
            ...SHIMMER_TEXT_CLASSES
          )}
        >
          Finishing without you…
        </Text>
      );
    }

    return (
      <YStack className="gap-1">
        <StatusBox
          icon={
            isReply ? (
              <Icons.chat className="size-4" />
            ) : (
              <Icons.shieldCheck className="size-4" />
            )
          }
          title={isReply ? 'Needs you' : 'Waiting for your approval'}
          body={
            isReply
              ? wait.reason
              : `The run goes on once you decide. It waits until ${formatDeadline(deadline)}.`
          }
        >
          <XStack className="mt-3 flex-wrap items-center gap-x-3 gap-y-2">
            {isReply && chatId && (
              <Button
                size="sm"
                isLoading={isOpening}
                onClick={() => void openChat(chatId)}
              >
                Reply
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => finish(false)}>
              Finish without me
            </Button>
            <TextLink onClick={() => finish(true)}>
              Finish, and don&apos;t ask again for this routine
            </TextLink>
          </XStack>
        </StatusBox>
        {!isReply && chatId && (
          <ReportActionCards chatId={chatId} renderedText="" />
        )}
      </YStack>
    );
  }
);

const CONTINUE_FAILED_MESSAGE = 'Could not continue without the device';

const DeviceRow = ({
  deviceSlug,
  className,
  children,
}: {
  deviceSlug: string;
  className: string;
  children: ReactNode;
}) => (
  <li className={cn('flex items-center gap-2', className)}>
    <DeviceSlug className="min-w-0 flex-1">{deviceSlug}</DeviceSlug>
    {children}
  </li>
);

const continueLabel = (waitingCount: number) => {
  if (waitingCount === 1) return 'Continue without it';
  return waitingCount === 2
    ? 'Continue without both'
    : 'Continue without all of them';
};

/** What a run in progress waits for, until when, and the way to stop waiting */
const WaitingLine = observer(({ run }: { run: RoutineRunViewModel }) => {
  const { reports } = useStores();
  const [skippingSlug, setSkippingSlug] = useState<string | null>(null);
  const isFinishing = reports.isOperationPending(
    'continueWithoutDevice',
    run.id
  );
  const isSkipping = reports.isOperationPending('skipWaitingDevice', run.id);
  const isActionPending = isFinishing || isSkipping;
  const waiting = run.waitingOn;
  const [firstDevice] = waiting;
  const hasSeveral = waiting.length > 1;
  const icon = (
    <DeviceIcon
      platform={firstDevice.platform}
      className="size-3.5 animate-pulse text-foreground-muted motion-reduce:animate-none"
    />
  );

  const continueWithoutAll = () =>
    void reports
      .continueWithoutDevice(run.id)
      .catch(() => toast.error(CONTINUE_FAILED_MESSAGE));

  const skipDevice = (deviceSlug: string) => {
    setSkippingSlug(deviceSlug);
    void reports
      .skipWaitingDevice(run.id, deviceSlug)
      .catch(() => toast.error(CONTINUE_FAILED_MESSAGE))
      .finally(() => setSkippingSlug(null));
  };

  if (isFinishing) {
    return (
      <XStack className="items-center gap-2 text-[13px]">
        {icon}
        <Text
          className={cn(
            'w-fit bg-clip-text text-[13px] text-transparent',
            ...SHIMMER_TEXT_CLASSES
          )}
        >
          Finishing without{' '}
          {hasSeveral ? deviceCount(waiting.length) : firstDevice.deviceSlug}…
        </Text>
      </XStack>
    );
  }

  const earliestDeadline = new Date(
    Math.min(...waiting.map((device) => device.waitsUntil.getTime()))
  );

  return (
    <YStack className="gap-1.5 text-[13px] text-foreground-muted">
      <XStack className="items-start gap-2">
        <span className="flex h-5 items-center">{icon}</span>
        {/* Below ~400px the link wraps under the text, not under the icon */}
        <XStack className="min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
          <span className="min-w-0 flex-1 basis-52 leading-5">
            Waiting for{' '}
            {hasSeveral ? (
              deviceCount(waiting.length)
            ) : (
              <DeviceSlug>{firstDevice.deviceSlug}</DeviceSlug>
            )}{' '}
            until {formatDeadline(earliestDeadline)}
          </span>
          <TextLink disabled={isActionPending} onClick={continueWithoutAll}>
            {continueLabel(waiting.length)}
          </TextLink>
        </XStack>
      </XStack>
      {(hasSeveral || run.ranWithout.length > 0) && (
        <ul className="flex flex-col gap-1.5">
          {hasSeveral &&
            waiting.map((device) => {
              const isSkippingDevice =
                isSkipping && skippingSlug === device.deviceSlug;
              return (
                <DeviceRow
                  key={device.deviceSlug}
                  deviceSlug={device.deviceSlug}
                  className="pl-[22px]"
                >
                  <span className="whitespace-nowrap">
                    {platformName(device.platform)} · offline
                  </span>
                  <TextLink
                    aria-label={`Skip ${device.deviceSlug}`}
                    aria-busy={isSkippingDevice || undefined}
                    className={cn(
                      isSkippingDevice &&
                        'animate-pulse motion-reduce:animate-none'
                    )}
                    disabled={isActionPending}
                    onClick={() => skipDevice(device.deviceSlug)}
                  >
                    Skip
                  </TextLink>
                </DeviceRow>
              );
            })}
          {run.ranWithout.map((device) => (
            <DeviceRow
              key={device.deviceSlug}
              deviceSlug={device.deviceSlug}
              className="pl-[22px]"
            >
              <span className="whitespace-nowrap">Skipped</span>
            </DeviceRow>
          ))}
        </ul>
      )}
    </YStack>
  );
});

/** The note after a finished run's report that names the devices it went on without */
export const SkippedDevicesNote = ({
  devices,
}: {
  devices: readonly RanWithoutDevice[];
}) => {
  const [firstDevice] = devices;
  const hasSeveral = devices.length > 1;

  return (
    <YStack className="gap-2 border-t border-border pt-3.5 text-[13px] text-foreground-muted">
      <XStack className="items-center gap-2.5">
        <DeviceIcon
          platform={firstDevice.platform}
          className="size-3.5 text-foreground-muted"
        />
        <span className="min-w-0 flex-1">
          Skipped {deviceCount(devices.length)}.
          {!hasSeveral && (
            <>
              {' '}
              <DeviceSlug>{firstDevice.deviceSlug}</DeviceSlug> was offline.
            </>
          )}
        </span>
      </XStack>
      {hasSeveral && (
        <ul className="flex flex-col gap-2">
          {devices.map((device) => (
            <DeviceRow
              key={device.deviceSlug}
              deviceSlug={device.deviceSlug}
              className="pl-6"
            >
              <span className="whitespace-nowrap">
                {platformName(device.platform)} · was offline
              </span>
            </DeviceRow>
          ))}
        </ul>
      )}
    </YStack>
  );
};

export const RoutineContentLoading = () => (
  <YStack
    className={cn(
      'mx-auto min-h-[70vh] w-full gap-4 bg-transparent px-3 py-8 md:px-12 md:py-12',
      uiStore.chatWidth
    )}
    aria-label="Routine run content is loading"
    aria-busy="true"
  >
    <Skeleton variant="translucent" className="h-7 w-48 max-w-full" />
    <Skeleton variant="translucent" className="h-64 w-full rounded-xl" />
  </YStack>
);

const getStatusBox = (run: RoutineRunViewModel) => {
  if (run.status === 'error') {
    return (
      <StatusBox
        role="alert"
        className="border-red-400/30 bg-red-50/60 dark:bg-red-950/20"
        icon={<Icons.info className="size-4 text-red-600 dark:text-red-400" />}
        title="This run needs attention"
        body="The routine could not finish. Try running it again from the runs panel."
      />
    );
  }

  if (run.status === 'clear') {
    return (
      <StatusBox
        icon={<Icons.check className="size-4" />}
        title="No new findings"
        body="This run finished and found nothing new to report."
      />
    );
  }

  return null;
};

const RunStatusMessage = ({
  run,
  placementClassName,
}: {
  run: RoutineRunViewModel;
  placementClassName: string;
}) => {
  const box = getStatusBox(run);
  return box && <div className={placementClassName}>{box}</div>;
};

export const RoutineRunEntry = observer(
  ({
    run,
    reportBody,
    actions,
    isRefreshingNarrative = false,
    onCanvasReadyChange,
  }: RoutineRunEntryProps) => {
    void settingsStore.timeFormat;

    const [areStepsExpanded, setAreStepsExpanded] = useState(false);
    const [readyCanvasArtifactKey, setReadyCanvasArtifactKey] = useState<
      string | null
    >(null);
    const { layout, isCanvas, artifact, artifactId } = reportBody;
    const steps = run.transcript ?? [];
    const hasSteps = steps.length > 0;
    const stepCount = run.stepCount ?? steps.length;
    const hasNarrative =
      run.narrative.trim().length > 0 &&
      run.status !== 'clear' &&
      run.status !== 'running';
    const showExpandedSteps = hasSteps && areStepsExpanded;
    const showArtifact = layout !== 'text';
    const showNarrative =
      layout !== 'artifact' && layout !== 'pending' && hasNarrative;
    const showSkippedDevices = run.ranWithout.length > 0;
    const showUsage =
      !!run.usage ||
      (!!run.chatId &&
        userStore.isInternalUser &&
        settingsStore.costFooterEnabled);
    const canvasArtifactKey =
      isCanvas && artifact?.status === 'ready'
        ? `${artifactId}:${artifact.file.updatedAt?.toISOString() ?? ''}`
        : null;
    useLayoutEffect(() => {
      setReadyCanvasArtifactKey((readyKey) =>
        readyKey === canvasArtifactKey ? readyKey : null
      );
    }, [canvasArtifactKey]);
    const handleCanvasReadyChange = useCallback(
      (isReady: boolean) => {
        setReadyCanvasArtifactKey(isReady ? canvasArtifactKey : null);
      },
      [canvasArtifactKey]
    );
    // In a full-width pane the run's status keeps to reading width
    const statusPlacement = cn(
      (isCanvas || layout === 'pending') && 'mx-auto w-full max-w-[640px]'
    );
    const isCanvasLoading =
      isCanvas &&
      artifact?.status === 'ready' &&
      readyCanvasArtifactKey !== canvasArtifactKey;
    useLayoutEffect(() => {
      onCanvasReadyChange?.(!isCanvasLoading);
    }, [isCanvasLoading, onCanvasReadyChange]);

    const reachOut =
      run.status === 'needs_reply' || run.status === 'needs_approval'
        ? run.reachOut
        : null;

    if (
      layout === 'pending' &&
      run.status !== 'running' &&
      run.status !== 'waiting' &&
      !reachOut
    ) {
      return (
        <article
          className="w-full"
          aria-label={`Automated routine run from ${formatNoteDate(run.timestamp)}`}
          aria-busy="true"
        >
          <RoutineContentLoading />
        </article>
      );
    }

    return (
      <article
        className="w-full"
        aria-label={`Automated routine run from ${formatNoteDate(run.timestamp)}`}
        aria-busy={isRefreshingNarrative || isCanvasLoading || undefined}
      >
        {hasSteps && !isCanvasLoading && (
          <button
            type="button"
            aria-expanded={areStepsExpanded}
            aria-controls={`routine-run-steps-${run.id}`}
            className="focus-ring mb-5 flex items-center gap-2 rounded-md text-left sm:ml-9"
            onClick={() => setAreStepsExpanded((expanded) => !expanded)}
          >
            <Text className="text-sm text-foreground-muted">
              {stepCount} {stepCount === 1 ? 'step' : 'steps'}
            </Text>
            <Icons.chevronDown
              className={cn(
                'size-3.5 shrink-0 text-foreground-muted transition-transform',
                !areStepsExpanded && '-rotate-90'
              )}
            />
          </button>
        )}

        <YStack className={cn('gap-5 pl-0', !isCanvas && 'sm:pl-9')}>
          {reachOut?.waiting ? (
            <div className={statusPlacement}>
              <UserWaitBox
                key={run.id}
                run={run}
                wait={reachOut.waiting}
                deadline={reachOut.deadline}
              />
            </div>
          ) : run.status === 'waiting' ? (
            <>
              <div className={statusPlacement}>
                <WaitingLine key={run.id} run={run} />
              </div>
              {!isCanvas && <RunSkeletonLines />}
            </>
          ) : run.status === 'running' ? (
            <RunLoading />
          ) : (
            <>
              {!isCanvasLoading && (
                <RunStatusMessage
                  run={run}
                  placementClassName={statusPlacement}
                />
              )}

              {showExpandedSteps && !isCanvasLoading && (
                <YStack
                  id={`routine-run-steps-${run.id}`}
                  className="gap-2 rounded-lg border border-border p-3"
                >
                  {steps.map((entry) => (
                    <YStack key={entry.id} className="gap-0.5">
                      <Text className="text-xs text-foreground-subtle">
                        {entry.label}
                      </Text>
                      {entry.detail && (
                        <Text className="text-[11px] text-foreground-muted">
                          {entry.detail}
                        </Text>
                      )}
                    </YStack>
                  ))}
                </YStack>
              )}

              {showArtifact && (
                <div
                  className={cn(
                    'relative w-full',
                    isCanvasLoading && 'min-h-[70vh]'
                  )}
                >
                  {isCanvasLoading && <RoutineContentLoading />}
                  <div
                    className={cn(
                      isCanvas && 'transition-opacity duration-100',
                      isCanvasLoading
                        ? 'pointer-events-none absolute inset-x-0 top-0 opacity-0'
                        : isCanvas && 'opacity-100'
                    )}
                  >
                    <ReportArtifact
                      key={artifactId}
                      artifact={artifact}
                      chatId={run.chatId ?? undefined}
                      isWholeReport={layout === 'artifact'}
                      showCanvasExport={!isCanvas}
                      onCanvasReadyChange={handleCanvasReadyChange}
                    />
                  </div>
                </div>
              )}

              <CanvasActionCardPlacementProvider
                fileIds={artifactId ? [artifactId] : []}
              >
                {showNarrative && (
                  <div className="prose prose-bird dark:prose-invert">
                    <Markdown
                      id={`routine-run-${run.id}`}
                      content={run.narrative}
                      chatId={run.chatId ?? undefined}
                    />
                  </div>
                )}

                {run.chatId && (
                  <ReportActionCards
                    chatId={run.chatId}
                    renderedText={showNarrative ? run.narrative : ''}
                  />
                )}
              </CanvasActionCardPlacementProvider>

              {!isCanvas && (actions || showUsage) && (
                <YStack
                  className={cn(
                    'w-full gap-5',
                    isCanvas && 'mx-auto px-3 md:px-12',
                    isCanvas && uiStore.chatWidth
                  )}
                >
                  {actions}

                  {showUsage && (
                    <details className="text-xs text-foreground-muted">
                      <summary className="cursor-pointer select-none text-foreground-subtle marker:text-foreground-muted">
                        Usage details
                      </summary>
                      <YStack className="mt-2 gap-1">
                        {run.usage?.totalTokens !== undefined && (
                          <Text className="text-[11px] text-foreground-muted">
                            {run.usage.totalTokens.toLocaleString()} tokens
                          </Text>
                        )}
                        {run.usage?.costUsd !== undefined && (
                          <Text className="text-[11px] text-foreground-muted">
                            ${run.usage.costUsd.toFixed(4)}
                          </Text>
                        )}
                        {run.chatId &&
                          userStore.isInternalUser &&
                          settingsStore.costFooterEnabled && (
                            <ReportCostFooter chatId={run.chatId} />
                          )}
                      </YStack>
                    </details>
                  )}
                </YStack>
              )}

              {showSkippedDevices && !isCanvasLoading && (
                <div className={statusPlacement}>
                  <SkippedDevicesNote devices={run.ranWithout} />
                </div>
              )}
            </>
          )}

          {!isCanvasLoading && (
            <StaffBackendLabel
              backend={run.backend}
              className={cn(statusPlacement, 'text-xs text-foreground-muted')}
            />
          )}
        </YStack>
      </article>
    );
  }
);
